/**
 * Vista 4 · Sorteo de muestra.
 *
 * Asistente de tres pasos, cada uno bloqueado hasta que el anterior es válido.
 * La compuerta importa: sortear con un mapeo de columnas equivocado produce un
 * universo equivocado, y el sorteo decide a quién se audita.
 *
 * Muestreo aleatorio simple, como el acta que firma el área: el % de TODA la
 * población, redondeado hacia arriba y con el tope de la política. El `%
 * efectivo` se muestra porque es lo que el acta declara —la probabilidad que
 * tuvo cada uno de salir— y, por el redondeo, casi nunca es el objetivo exacto.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { FileText, RefreshCw } from 'lucide-react'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mpfn/ui'
import {
  Callout,
  ConfirmDialog,
  DataTable,
  FileUploadField,
  FilterBar,
  FormField,
  SearchBar,
  StatCard,
  StepperHeader,
  type DataTableColumn,
} from '@mpfn/patterns'
import { Casilla } from '../componentes/Conmutadores.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { urlDeActa } from '../api/cliente.js'
import {
  useAbrirBloque,
  useBloques,
  useCargarPadron,
  useConfirmarSorteo,
  useEjecutarSorteo,
  usePolitica,
  useSemillaSugerida,
  useSorteo,
} from '../api/consultas.js'
import { etiquetaDe, useCatalogos } from '../api/catalogos.js'
import type { Cobertura, EstratoSorteo, MapeoColumnas, SeleccionSorteo } from '../api/tipos.js'
import { irA } from '../navegacion/Enlace.js'
import { usePuede } from '../sesion/contexto.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'
import { MENOS_MOVIMIENTO, useMedia } from '../lib/medios.js'
import { dni as formatearDni, hoy, instante, numero, periodo as formatearPeriodo, porcentaje } from '../lib/formato.js'
import { periodosParaAbrir } from '../lib/periodos.js'

const PASOS = [
  { id: 'padron', label: 'Padrón' },
  { id: 'configurar', label: 'Configurar' },
  { id: 'muestra', label: 'Muestra' },
] as const

/**
 * DNI y nombres son obligatorios (`P-15`): el DNI siembra el aleatorio y sin
 * nombre no hay expediente. El resto describe al servidor en el acta y en el
 * expediente: se propone solo y se puede dejar sin columna.
 */
const COLUMNAS_MAPEO = [
  { clave: 'dni', etiqueta: 'DNI', obligatoria: true },
  { clave: 'nombres', etiqueta: 'Apellidos y nombres', obligatoria: true },
  { clave: 'regimen', etiqueta: 'Régimen laboral', obligatoria: false },
  { clave: 'distrito', etiqueta: 'Distrito fiscal', obligatoria: false },
  { clave: 'cargo', etiqueta: 'Cargo', obligatoria: false },
  { clave: 'motivo', etiqueta: 'Motivo', obligatoria: false },
  { clave: 'movimiento', etiqueta: 'Movimiento', obligatoria: false },
  { clave: 'convocatoria', etiqueta: 'Convocatoria', obligatoria: false },
  { clave: 'fechaIngreso', etiqueta: 'Fecha de ingreso', obligatoria: false },
  { clave: 'unidadEjecutora', etiqueta: 'Unidad ejecutora', obligatoria: false },
] as const satisfies ReadonlyArray<{ clave: keyof MapeoColumnas; etiqueta: string; obligatoria: boolean }>

/** El `Select` no admite una opción de valor vacío: «sin columna» necesita uno propio. */
const SIN_COLUMNA = 'sin-columna'

/**
 * Lo que la selección puede asegurar además del %, como lo pidió la jefatura
 * sobre el padrón: al menos uno de cada distrito fiscal, régimen y
 * convocatoria. Cada criterio necesita su columna en el mapeo.
 */
const CRITERIOS = [
  {
    codigo: 'distrito_fiscal',
    columna: 'distrito',
    etiqueta: 'Al menos uno por distrito fiscal',
    descripcion: 'Cada distrito fiscal del padrón tiene al menos un seleccionado.',
  },
  {
    codigo: 'regimen',
    columna: 'regimen',
    etiqueta: 'Al menos uno por régimen laboral',
    descripcion: 'Cada régimen —D.L. 1057, 728, 276…— tiene al menos un seleccionado.',
  },
  {
    codigo: 'convocatoria',
    columna: 'convocatoria',
    etiqueta: 'Al menos uno por convocatoria',
    descripcion: 'Cada número de convocatoria tiene al menos un seleccionado: «84-2026» y «084-2026» cuentan como una.',
  },
] as const satisfies ReadonlyArray<{
  codigo: Cobertura
  columna: keyof MapeoColumnas
  etiqueta: string
  descripcion: string
}>

/** Para el resultado: «23 de 23 distritos fiscales». */
const GRUPOS_CUBIERTOS: Readonly<Record<Cobertura, string>> = {
  distrito_fiscal: 'distritos fiscales',
  regimen: 'regímenes laborales',
  convocatoria: 'convocatorias',
}

export function Sorteo() {
  const catalogos = useCatalogos()
  const bloques = useBloques()
  const politica = usePolitica()
  const puedeConfirmar = usePuede('sorteo.confirmar')
  const puedeEjecutar = usePuede('sorteo.ejecutar')

  const [bloqueId, setBloqueId] = useState('')
  const [periodoElegido, setPeriodoElegido] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)
  const [hoja, setHoja] = useState('')
  const [mapeo, setMapeo] = useState<Partial<MapeoColumnas>>({})
  const [objetivo, setObjetivo] = useState('10')
  const [coberturas, setCoberturas] = useState<readonly Cobertura[]>([])
  const [semilla, setSemilla] = useState('')
  const [sorteoId, setSorteoId] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const anclaResultado = useRef<HTMLDivElement>(null)
  const sinMovimiento = useMedia(MENOS_MOVIMIENTO)

  const abrirBloque = useAbrirBloque()
  const cargarPadron = useCargarPadron()
  const ejecutar = useEjecutarSorteo()
  const confirmar = useConfirmarSorteo()
  const sugerida = useSemillaSugerida(puedeEjecutar)
  const resultado = useSorteo(sorteoId || undefined)

  const padron = cargarPadron.data

  // El mínimo del sorteo es configurable (`SN-7`): se toma de la política, no
  // de una constante del frontend.
  useEffect(() => {
    if (politica.data) setObjetivo(String(politica.data.porcentajeMinimoSorteo))
  }, [politica.data])

  useEffect(() => {
    if (sugerida.data && semilla === '') setSemilla(sugerida.data.semilla)
  }, [sugerida.data, semilla])

  useEffect(() => {
    if (padron?.mapeoPropuesto) setMapeo(padron.mapeoPropuesto)
  }, [padron])

  /**
   * Llevar la vista al resultado en cuanto el sorteo devuelve.
   *
   * `smooth` deja ver QUE la página se movió —si saltara, parecería que la
   * pantalla cambió sola—, salvo a quien pidió menos movimiento en su sistema:
   * `prefers-reduced-motion` existe porque a algunas personas el desplazamiento
   * animado les produce mareo, y ahí el salto seco es lo correcto.
   */
  useEffect(() => {
    if (sorteoId === '') return
    anclaResultado.current?.scrollIntoView({
      behavior: sinMovimiento ? 'auto' : 'smooth',
      block: 'start',
    })
  }, [sorteoId, sinMovimiento])

  const disponibles = useMemo(
    () => (bloques.data ?? []).filter((bloque) => bloque.estado === 'en_sorteo'),
    [bloques.data],
  )

  /**
   * Los meses que se pueden abrir, mirando los bloques **en el estado que
   * estén**: `disponibles` solo ve los que están en sorteo, y por eso la vista
   * ofrecía abrir un periodo que ya tenía bloque en fiscalización.
   */
  const abribles = useMemo(
    () => periodosParaAbrir(hoy(), (bloques.data ?? []).map((bloque) => bloque.periodo)),
    [bloques.data],
  )
  const periodoAAbrir = abribles.libres.includes(periodoElegido) ? periodoElegido : abribles.propuesto
  /** El más reciente, para cuando ya no queda mes por abrir. */
  const ultimoBloque = useMemo(
    () => (bloques.data ?? []).toSorted((a, b) => b.periodo.localeCompare(a.periodo))[0],
    [bloques.data],
  )

  /** DNI y nombres elegidos, y ninguna columna usada para dos campos. */
  const mapeoValido = useMemo(() => {
    const elegidas = COLUMNAS_MAPEO.map((columna) => mapeo[columna.clave]).filter((valor) => valor !== undefined)
    const obligatorias = COLUMNAS_MAPEO.filter((columna) => columna.obligatoria)
    return (
      obligatorias.every((columna) => mapeo[columna.clave] !== undefined) &&
      new Set(elegidas).size === elegidas.length
    )
  }, [mapeo])

  const minimo = politica.data?.porcentajeMinimoSorteo
  const porcentajeBajo = minimo !== undefined && Number(objetivo) < minimo

  // Sortear exige un padrón cargado EN ESTA SESIÓN: abrir el sorteo pendiente
  // que dejó otra persona no basta, porque el padrón no viaja con él.
  const listoParaSortear = padron !== undefined && mapeoValido
  const paso = sorteoId !== '' ? 2 : listoParaSortear ? 1 : 0

  // Un criterio marcado cuya columna se quitó del mapeo no se puede asegurar:
  // no se envía, y su casilla se muestra desmarcada.
  const coberturasPedidas = CRITERIOS.filter(
    (criterio) => coberturas.includes(criterio.codigo) && mapeo[criterio.columna] !== undefined,
  ).map((criterio) => criterio.codigo)

  return (
    <Pagina
      titulo="Selección de muestra"
      descripcion="La muestra se deriva de la semilla y el DNI: con los mismos datos, el resultado se reproduce."
    >

      {/* Solo la cabecera del asistente: la navegación entre pasos la gobierna
          el estado del dato, no un botón de «siguiente». */}
      <StepperHeader steps={PASOS} current={paso} />

      {/* ── Paso 1 · Padrón ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>1 · Cargar el padrón de altas del mes</CardTitle>
        </CardHeader>
        <CardContent className="sigfisc-formulario">
          <FormField id="bloque-periodo" label="Bloque" required hint="Un bloque por mes.">
            <Select
              value={bloqueId}
              onValueChange={(valor) => {
                setBloqueId(valor)
                // Si el bloque ya tiene un sorteo pendiente —lo ejecutó otra
                // persona, o esta misma en otra sesión—, se abre: quien confirma
                // ve lo que se sorteó, sin tener que repetirlo (FLUJO_ACTUAL §13.7).
                setSorteoId(disponibles.find((bloque) => bloque.id === valor)?.sorteoId ?? '')
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Elija el bloque" />
              </SelectTrigger>
              <SelectContent>
                {disponibles.map((bloque) => (
                  <SelectItem key={bloque.id} value={bloque.id}>
                    {formatearPeriodo(bloque.periodo)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {/**
           * Abrir un mes que ya tiene bloque es una acción que el servidor
           * rechaza con «ya existe un bloque para setiembre», y un botón que no
           * puede funcionar no debe estar: solo se ofrecen los meses libres.
           * Si no queda ninguno, se lleva al último bloque. Mientras la lista
           * carga no se sabe si hay bloques: no se dice que no los hay.
           */}
          {bloques.data !== undefined && disponibles.length === 0 &&
            (periodoAAbrir !== undefined ? (
              <Callout
                tone="info"
                title="No hay ningún bloque abierto para seleccionar la muestra"
                action={
                  <div className="sigfisc-abrir-bloque">
                    <Select value={periodoAAbrir} onValueChange={setPeriodoElegido}>
                      <SelectTrigger aria-label="Mes de las altas">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {abribles.libres.map((periodo) => (
                          <SelectItem key={periodo} value={periodo}>
                            {formatearPeriodo(periodo)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="secondary"
                      loading={abrirBloque.isPending}
                      onClick={() => {
                        abrirBloque.mutate(periodoAAbrir, {
                          onSuccess: (datos) => {
                            setBloqueId(datos.bloque.id)
                            avisarExito(
                              `Se abrió el bloque ${formatearPeriodo(datos.bloque.periodo)}.`,
                            )
                          },
                          onError: (error) => {
                            avisarError(error, 'No se pudo abrir el bloque')
                          },
                        })
                      }}
                    >
                      Abrir {formatearPeriodo(periodoAAbrir)}
                    </Button>
                  </div>
                }
              >
                Elija el mes de las altas: es el periodo de vinculación que imprime el acta. Se
                propone el último mes cerrado, porque sus altas ya están completas.
              </Callout>
            ) : (
              ultimoBloque && (
                <Callout
                  tone="info"
                  title={`El bloque de ${formatearPeriodo(ultimoBloque.periodo)} ya está en fiscalización`}
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        irA('bloque', { periodo: ultimoBloque.periodo })
                      }}
                    >
                      Ver el bloque
                    </Button>
                  }
                >
                  Los últimos meses ya tienen su bloque: hay uno por mes.
                </Callout>
              )
            ))}

          <FileUploadField
            id="padron"
            value={archivo}
            accept=".xlsx"
            acceptLabel="XLSX · un registro por fila"
            maxSizeMb={20}
            variant="dropzone"
            disabled={bloqueId === ''}
            onError={(mensaje) => {
              avisarError(new Error(mensaje), 'El archivo no es válido')
            }}
            onChange={(elegido) => {
              setArchivo(elegido)
              setSorteoId('')
              if (!elegido || bloqueId === '') return
              cargarPadron.mutate(
                { bloqueId, archivo: elegido },
                {
                  onError: (error) => {
                    avisarError(error, 'No se pudo leer el padrón')
                  },
                },
              )
            }}
          />

          {padron && (
            <>
              <p className="sigfisc-nota">
                <strong>{padron.nombreArchivo}</strong> · hoja {padron.hoja} ·{' '}
                {numero(padron.totalFilas)} filas · {numero(padron.cabeceras.length)} columnas
              </p>

              {padron.celdasReparadas > 0 && (
                <Callout
                  tone="info"
                  title={`Se corrigieron ${plural(padron.celdasReparadas, 'celda', 'celdas')} con caracteres dañados`}
                >
                  El padrón trae las tildes, la Ñ y el «N°» cambiados por símbolos como «═», «Ð» o
                  «░». Se corrigen al leer; el archivo original se conserva tal cual, con su huella.
                </Callout>
              )}

              {padron.hojas.length > 1 && (
                <FormField id="hoja" label="Hoja">
                  <Select
                    value={hoja || padron.hoja}
                    onValueChange={(valor) => {
                      setHoja(valor)
                      if (archivo && bloqueId !== '') {
                        cargarPadron.mutate({ bloqueId, archivo, hoja: valor })
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {padron.hojas.map((nombre) => (
                        <SelectItem key={nombre} value={nombre}>
                          {nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              )}

              <fieldset className="sigfisc-mapeo">
                <legend>Mapeo de columnas · DNI y apellidos y nombres son obligatorios</legend>
                {COLUMNAS_MAPEO.map((columna) => {
                  const elegida = mapeo[columna.clave]
                  return (
                    <FormField
                      key={columna.clave}
                      id={`mapeo-${columna.clave}`}
                      label={columna.etiqueta}
                      required={columna.obligatoria}
                    >
                      <Select
                        value={elegida !== undefined ? String(elegida) : columna.obligatoria ? '' : SIN_COLUMNA}
                        onValueChange={(valor) => {
                          setMapeo((previo) => {
                            const siguiente = { ...previo }
                            if (valor === SIN_COLUMNA) delete siguiente[columna.clave]
                            else siguiente[columna.clave] = Number(valor)
                            return siguiente
                          })
                          setSorteoId('')
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Elija la columna" />
                        </SelectTrigger>
                        <SelectContent>
                          {!columna.obligatoria && <SelectItem value={SIN_COLUMNA}>Sin columna</SelectItem>}
                          {padron.cabeceras.map((cabecera, indice) => (
                            <SelectItem key={`${cabecera}-${String(indice)}`} value={String(indice)}>
                              {cabecera || `Columna ${String(indice + 1)}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormField>
                  )
                })}
              </fieldset>

              {!mapeoValido && (
                <Callout tone="warning" title="Falta completar el mapeo">
                  Elija la columna del DNI y la de apellidos y nombres, y no use una misma columna
                  para dos campos.
                </Callout>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Paso 2 · Configurar ───────────────────────────────────────── */}
      <Card data-inactiva={listoParaSortear ? 'no' : 'si'}>
        <CardHeader>
          <CardTitle>2 · Configurar la selección</CardTitle>
        </CardHeader>
        <CardContent className="sigfisc-formulario">
          <p className="sigfisc-nota">
            Muestreo aleatorio simple: sale el % indicado de toda la población, redondeado hacia
            arriba
            {politica.data
              ? `, sin pasar del cupo de ${numero(politica.data.maximoLegajosSemestre)} legajos por semestre. La norma exige al menos el ${String(politica.data.porcentajeMinimoSorteo)} %.`
              : '.'}{' '}
            Además, puede asegurar al menos un seleccionado de cada distrito fiscal, régimen o
            convocatoria: si hay más grupos que el %, la muestra crece hasta cubrirlos.
          </p>

          <div className="sigfisc-fila-campos sigfisc-fila-campos--3">
            <FormField
              id="objetivo"
              label="% de la población"
              required
              {...(porcentajeBajo ? { error: `La norma exige al menos el ${String(minimo)} %.` } : {})}
            >
              <Input
                type="number"
                min={minimo ?? 1}
                max={100}
                value={objetivo}
                disabled={!listoParaSortear}
                onChange={(evento) => {
                  setObjetivo(evento.target.value)
                }}
              />
            </FormField>

            <FormField
              id="semilla"
              label="Semilla"
              required
              hint="Queda registrada: con la misma semilla y el mismo padrón, la selección se reproduce."
            >
              <Input
                value={semilla}
                disabled={!listoParaSortear}
                onChange={(evento) => {
                  setSemilla(evento.target.value)
                }}
              />
            </FormField>

            {/* Con icono: sin él, un control blanco de 40 px al lado de dos
                inputs de 40 px se leía como un tercer campo, no como una
                acción. El icono es lo que lo distingue de un input. */}
            <Button
              variant="secondary"
              disabled={!listoParaSortear}
              loading={sugerida.isFetching}
              onClick={() => {
                void sugerida.refetch().then((respuesta) => {
                  if (respuesta.data) setSemilla(respuesta.data.semilla)
                })
              }}
            >
              <RefreshCw size={16} aria-hidden="true" />
              Nueva semilla
            </Button>
          </div>

          <fieldset className="sigfisc-criterios">
            <legend>Asegurar además, al menos un seleccionado</legend>
            {CRITERIOS.map((criterio) => {
              const conColumna = mapeo[criterio.columna] !== undefined
              return (
                <Casilla
                  key={criterio.codigo}
                  etiqueta={criterio.etiqueta}
                  marcado={coberturasPedidas.includes(criterio.codigo)}
                  deshabilitado={!listoParaSortear || !conColumna}
                  descripcion={
                    conColumna || !listoParaSortear
                      ? criterio.descripcion
                      : 'Elija primero su columna en el mapeo del padrón.'
                  }
                  onCambio={(marcado) => {
                    setCoberturas((previas) =>
                      marcado
                        ? [...previas.filter((codigo) => codigo !== criterio.codigo), criterio.codigo]
                        : previas.filter((codigo) => codigo !== criterio.codigo),
                    )
                    setSorteoId('')
                  }}
                />
              )
            })}
          </fieldset>

          <Button
            variant="primary"
            loading={ejecutar.isPending}
            disabled={!listoParaSortear || porcentajeBajo || semilla.trim() === ''}
            onClick={() => {
              // `mapeoValido` garantiza el DNI y los nombres.
              const completo = mapeo as MapeoColumnas
              ejecutar.mutate(
                {
                  bloqueId,
                  semilla: semilla.trim(),
                  porcentajeObjetivo: Number(objetivo),
                  mapeo: completo,
                  ...(hoja ? { hoja } : {}),
                  coberturas: coberturasPedidas,
                },
                {
                  onSuccess: (datos) => {
                    setSorteoId(datos.sorteo.id)
                    avisarExito(
                      `Muestra de ${plural(datos.sorteo.muestra, 'caso', 'casos')} sobre ${numero(
                        datos.sorteo.universoEfectivo,
                      )} del universo efectivo.`,
                    )
                  },
                  onError: (error) => {
                    avisarError(error, 'No se pudo generar la selección de muestra')
                  },
                },
              )
            }}
          >
            Generar selección de muestra
          </Button>
        </CardContent>
      </Card>

      {/* ── Paso 3 · Muestra ──────────────────────────────────────────── */}
      {/**
       * El resultado aparece DEBAJO del pliegue: el paso 2 ocupa la pantalla
       * entera, así que al pulsar «Sortear muestra» no cambiaba nada visible y
       * había que adivinar que la respuesta estaba más abajo. El ancla lo trae
       * a la vista; `ref` y no `id`, para no inventar un identificador global.
       */}
      <div ref={anclaResultado} />

      {sorteoId !== '' && (
        <Datos3
          sorteoId={sorteoId}
          busqueda={busqueda}
          onBuscar={setBusqueda}
          puedeConfirmar={puedeConfirmar}
          confirmando={confirmando}
          onConfirmar={() => {
            setConfirmando(true)
          }}
          onCerrar={() => {
            setConfirmando(false)
          }}
          confirmarPendiente={confirmar.isPending}
          onEjecutarConfirmacion={() => {
            confirmar.mutate(sorteoId, {
              onSuccess: (datos) => {
                setConfirmando(false)
                const bloque = (bloques.data ?? []).find((item) => item.id === datos.bloqueId)

                // El «qué sigue» NO va en el aviso: se desvanecería. Va como
                // un aviso persistente en la propia vista del bloque, donde
                // sirve siempre y no solo en los segundos posteriores a
                // confirmar. Es la regla de Recuperos §10.
                avisarExito(
                  `Se crearon ${plural(datos.expedientesCreados, 'expediente', 'expedientes')}.`,
                  `Se emitió el acta N.° ${datos.numeroActa}.`,
                )
                if (bloque) irA('bloque', { periodo: bloque.periodo })
              },
              onError: (error) => {
                setConfirmando(false)
                avisarError(error, 'No se pudo confirmar la selección de muestra')
              },
            })
          }}
          etiquetaMotivoRechazo={(motivo) => etiquetaDe(catalogos.motivosRechazo, motivo)}
          resultado={resultado}
        />
      )}
    </Pagina>
  )
}

/** Paso 3 extraído para que el componente principal quepa de una lectura. */
function Datos3({
  busqueda,
  onBuscar,
  puedeConfirmar,
  confirmando,
  onConfirmar,
  onCerrar,
  confirmarPendiente,
  onEjecutarConfirmacion,
  etiquetaMotivoRechazo,
  resultado,
}: {
  sorteoId: string
  busqueda: string
  onBuscar: (valor: string) => void
  puedeConfirmar: boolean
  confirmando: boolean
  onConfirmar: () => void
  onCerrar: () => void
  confirmarPendiente: boolean
  onEjecutarConfirmacion: () => void
  etiquetaMotivoRechazo: (motivo: string) => string
  resultado: ReturnType<typeof useSorteo>
}) {
  const columnasReparto: ReadonlyArray<DataTableColumn<EstratoSorteo>> = [
    { id: 'regimen', header: 'Régimen', cell: (fila) => fila.regimen || 'Sin régimen' },
    { id: 'distrito', header: 'Distrito', cell: (fila) => fila.distrito || 'Sin distrito', hideOnMobile: true },
    { id: 'universo', header: 'Universo', cell: (fila) => numero(fila.universo), align: 'right' },
    { id: 'muestra', header: 'Seleccionados', cell: (fila) => numero(fila.muestra), align: 'right' },
    {
      id: 'efectivo',
      header: '%',
      align: 'right',
      cell: (fila) => porcentaje(fila.universo === 0 ? 0 : (fila.muestra / fila.universo) * 100),
    },
  ]

  const columnasSeleccion: ReadonlyArray<DataTableColumn<SeleccionSorteo>> = [
    { id: 'orden', header: 'N°', cell: (fila) => numero(fila.orden), align: 'right' },
    { id: 'dni', header: 'DNI', cell: (fila) => formatearDni(fila.dni) },
    { id: 'nombres', header: 'Trabajador', cell: (fila) => fila.nombres },
    { id: 'motivo', header: 'Motivo', cell: (fila) => fila.motivo ?? '—', hideOnMobile: true },
    { id: 'distrito', header: 'Distrito', cell: (fila) => fila.distrito || '—', hideOnMobile: true },
    { id: 'cargo', header: 'Puesto', cell: (fila) => fila.cargo ?? '—', hideOnMobile: true },
    {
      id: 'aleatorio',
      header: 'Aleatorio',
      cell: (fila) => fila.valorAleatorio.toFixed(6),
      align: 'right',
      hideOnMobile: true,
    },
  ]

  return (
    <Datos estado={resultado}>
      {(datos) => {
        const { sorteo } = datos
        const seleccionados = datos.selecciones.filter((fila) => fila.seleccionado)
        const filtrados = busqueda.trim()
          ? seleccionados.filter((fila) =>
              `${fila.nombres} ${fila.dni}`.toLowerCase().includes(busqueda.trim().toLowerCase()),
            )
          : seleccionados
        const excluidos = datos.rechazos.filter((fila) => fila.motivo === 'ya_fiscalizado')
        const defectuosos = datos.rechazos.filter((fila) => fila.motivo !== 'ya_fiscalizado')
        const metodoAnterior = sorteo.metodo === 'estratificado'
        // Lo que quedaba del cupo semestral al sortear (150 menos lo ya
        // fiscalizado en el semestre). Agotado, no sale nadie.
        const cupoSemestre =
          sorteo.topeSemestral !== undefined
            ? Math.max(0, sorteo.topeSemestral - (sorteo.fiscalizadosSemestre ?? 0))
            : undefined
        const sinCupo = cupoSemestre === 0 && sorteo.universoEfectivo > 0

        return (
          <>
            <p className="sigfisc-nota">
              {metodoAnterior ? 'Selección por estratos (método anterior)' : 'Muestreo aleatorio simple'} · semilla{' '}
              <strong>{sorteo.semilla}</strong> · {sorteo.padronNombreArchivo}, hoja {sorteo.padronHoja} · generada
              el {instante(sorteo.ejecutadoEn)}
            </p>

            {/* Lo que se aseguró además del %, y a cuántos grupos se llegó. */}
            {sorteo.coberturas.length > 0 && (
              <p className="sigfisc-nota">
                Con al menos un seleccionado por grupo:{' '}
                {sorteo.coberturas
                  .map(
                    (cobertura) =>
                      `${numero(cobertura.cubiertos)} de ${numero(cobertura.grupos)} ${GRUPOS_CUBIERTOS[cobertura.criterio]}`,
                  )
                  .join(' · ')}
              </p>
            )}
            {sorteo.coberturas.some((cobertura) => cobertura.cubiertos < cobertura.grupos) && (
              <Callout tone="warning" title="El cupo del semestre no alcanzó para todos los grupos">
                La muestra llegó al tope de legajos del semestre antes de cubrir cada grupo.
              </Callout>
            )}

            {/* El acta con el diseño de la de julio. Antes de confirmar es un
                borrador sin número, para revisarla; al confirmar se emite la
                oficial con el correlativo del año. */}
            {!metodoAnterior && (
              <div className="sigfisc-acciones">
                <Button variant="secondary" size="sm" asChild>
                  <a href={urlDeActa(sorteo.id)} target="_blank" rel="noopener noreferrer">
                    <FileText size={14} aria-hidden="true" />
                    {sorteo.numeroActa !== undefined ? `Ver el acta N.° ${sorteo.numeroActa}` : 'Ver el acta (borrador)'}
                  </a>
                </Button>
              </div>
            )}

            <div className="sigfisc-kpis">
              <StatCard label="Padrón" value={numero(sorteo.universoBruto)} detail="filas leídas" />
              <StatCard
                label="Universo efectivo"
                value={numero(sorteo.universoEfectivo)}
                detail={`${numero(sorteo.registrosRechazados)} rechazadas · ${numero(sorteo.excluidosPorFiscalizacionPrevia)} ya fiscalizadas`}
              />
              <StatCard
                label="Muestra"
                value={numero(sorteo.muestra)}
                detail={
                  cupoSemestre !== undefined && sorteo.topeSemestral !== undefined
                    ? `cupo del semestre: ${numero(cupoSemestre)} de ${numero(sorteo.topeSemestral)}`
                    : `${numero(sorteo.estratos)} estratos`
                }
              />
              <StatCard
                label="% efectivo"
                value={porcentaje(sorteo.porcentajeEfectivo)}
                detail={`objetivo ${porcentaje(sorteo.porcentajeObjetivo, 0)}`}
              />
            </div>

            {(sorteo.celdasReparadas > 0 || sorteo.dniCompletados > 0) && (
              <Callout tone="info" title="Correcciones al leer el padrón">
                <ul className="sigfisc-avisos">
                  {sorteo.celdasReparadas > 0 && (
                    <li>
                      {plural(sorteo.celdasReparadas, 'celda', 'celdas')} con caracteres dañados
                      (tildes, Ñ, «N°»)
                    </li>
                  )}
                  {sorteo.dniCompletados > 0 && (
                    <li>{plural(sorteo.dniCompletados, 'DNI', 'DNI')} sin el cero inicial, completados a ocho dígitos</li>
                  )}
                </ul>
                El archivo original se conserva tal cual, con su huella.
              </Callout>
            )}

            {defectuosos.length > 0 && (
              <Callout tone="warning" title={`${plural(defectuosos.length, 'fila rechazada', 'filas rechazadas')}`}>
                <ul className="sigfisc-avisos">
                  {[...new Set(defectuosos.map((fila) => fila.motivo))].map((motivo) => (
                    <li key={motivo}>
                      {etiquetaMotivoRechazo(motivo)} ·{' '}
                      {numero(defectuosos.filter((fila) => fila.motivo === motivo).length)}
                    </li>
                  ))}
                </ul>
              </Callout>
            )}

            {excluidos.length > 0 && (
              <Callout tone="info" title={`${plural(excluidos.length, 'excluido', 'excluidos')} por fiscalización previa`}>
                Ya tienen expediente en un bloque anterior, así que no vuelven a entrar al universo.
              </Callout>
            )}

            <Card>
              <CardHeader>
                <CardTitle>{metodoAnterior ? 'Resumen por estrato' : 'Reparto por régimen y distrito'}</CardTitle>
              </CardHeader>
              <CardContent>
                {!metodoAnterior && (
                  <p className="sigfisc-nota">
                    Es informativo: la selección se hace sobre toda la población, así que un grupo
                    pequeño puede quedar sin seleccionados, salvo que se haya asegurado su cobertura.
                  </p>
                )}
                <DataTable
                  columns={columnasReparto}
                  rows={datos.estratos}
                  rowKey={(fila) => `${fila.regimen}|${fila.distrito}`}
                  empty={{ title: 'Sin régimen ni distrito en el padrón' }}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Seleccionados</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  columns={columnasSeleccion}
                  rows={filtrados}
                  rowKey={(fila) => String(fila.filaPadron)}
                  toolbar={
                    <FilterBar
                      primary={
                        <SearchBar
                          value={busqueda}
                          onChange={onBuscar}
                          label="Buscar en la muestra"
                          placeholder="Nombre o DNI"
                        />
                      }
                    />
                  }
                  empty={{ title: 'Ningún seleccionado coincide con la búsqueda' }}
                />
              </CardContent>
            </Card>

            {sorteo.confirmadoEn !== undefined ? (
              <Callout tone="good" title="Selección confirmada">
                Los expedientes ya se crearon
                {sorteo.numeroActa !== undefined ? ` y se emitió el acta N.° ${sorteo.numeroActa}` : ''}. El
                padrón queda conservado con su huella.
              </Callout>
            ) : sinCupo ? (
              <Callout tone="warning" title="Se agotó el cupo del semestre">
                Este semestre ya se fiscalizaron {numero(sorteo.fiscalizadosSemestre)} legajos, el tope
                de la política ({numero(sorteo.topeSemestral)}). Con esta selección no sale nadie y no
                hay muestra que confirmar.
              </Callout>
            ) : metodoAnterior ? (
              <Callout tone="warning" title="Esta selección se hizo con el método anterior">
                Se seleccionó por estratos, con un mínimo de un caso por régimen y distrito, y su
                muestra sale inflada. No se puede confirmar: vuelva a cargar el padrón y genere la
                selección; ahora sale por muestreo aleatorio simple.
              </Callout>
            ) : puedeConfirmar ? (
              <>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={onConfirmar}
                >
                  Confirmar y crear {plural(sorteo.muestra, 'expediente', 'expedientes')}
                </Button>
                <ConfirmDialog
                  open={confirmando}
                  onOpenChange={(abierto) => {
                    if (!abierto) onCerrar()
                  }}
                  title="Confirmar la selección de muestra"
                  description={`Semilla ${sorteo.semilla} · universo efectivo ${numero(
                    sorteo.universoEfectivo,
                  )} · muestra ${numero(sorteo.muestra)} (${porcentaje(
                    sorteo.porcentajeEfectivo,
                  )}). Se crearán los expedientes, se emitirá el acta con el número que siga y la selección no se podrá repetir.`}
                  confirmLabel={`Crear ${plural(sorteo.muestra, 'expediente', 'expedientes')}`}
                  loading={confirmarPendiente}
                  onConfirm={onEjecutarConfirmacion}
                />
              </>
            ) : (
              <Callout tone="info" title="Falta confirmar">
                La muestra está lista. Confirmarla y crear los expedientes corresponde a quien tenga
                el permiso de confirmación de la selección de muestra.
              </Callout>
            )}
          </>
        )
      }}
    </Datos>
  )
}
